import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Minus, Search, Loader2, ShoppingBag, Check, AlertTriangle, Trash2, Package } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useMenuCategories, useMenuItems, usePOSRetailProducts } from '../../lib/menu-hooks';
import { ProductDetailModal } from '../restaurant/ProductDetailModal';
import { ArmaTuPlatoPanel } from '../restaurant/ArmaTuPlatoPanel';
import { formatCurrency, cn } from '../../lib/utils';
import type { MenuItem, CartItemOption } from '../../lib/types';
import { createNatuCafeAdditionPrintJob } from '../../lib/print-additions';
interface PendingItem {
  id: string;
  menuItem: MenuItem;
  quantity: number;
  options: CartItemOption[];
  notes: string;
}
interface CabanaAddItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
  orderId: string | null;
  restaurantId: number | string | undefined;
  /** Called after items are successfully added so the parent can refetch */
  onAdded: () => void;
}
/**
 * Cabaña Parrillera — exclusive cashier tool to ADD products to an
 * existing open table order. Mirrors create-order.ts insert shape so
 * the KDS picks up the new items automatically.
 *
 * Safety:
 *  - Only inserts new order_items + order_item_options rows.
 *  - Updates orders.subtotal / orders.total based on the new active
 *    items + the existing delivery_fee (does not touch other columns).
 *  - Does NOT mutate order status, payments, or any item already in
 *    the order. If the order is already paid/closed, the parent
    decides whether to expose this modal.
 */
export function CabanaAddItemsModal({
  isOpen,
  onClose,
  orderId,
  restaurantId,
  onAdded
}: CabanaAddItemsModalProps) {
  // ─── Rancho-only flags ────────────────────────────────────────────
  const isRancho = String(restaurantId ?? '') === '28';
  const ARMA_TU_PLATO_ID = '__arma_tu_plato__';
  // ─── NatuCafé-only flags (Estantería tab) ─────────────────────────
  // Restaurant 19 ships a dedicated POS/Caja Estantería tab that lists
  // retail products (product_type='retail', show_in_pos=true). These
  // items have station_code=null on menu_items so they naturally never
  // hit any KDS. They're inserted as normal order_items with the
  // existing flow below — no schema changes required.
  const isNatuCafe = String(restaurantId ?? '') === '19';
  const ESTANTERIA_ID = '__estanteria__';
  const {
    categories
  } = useMenuCategories(restaurantId ? String(restaurantId) : undefined);
  const {
    items: menuItems,
    loading: menuLoading
  } = useMenuItems(restaurantId ? String(restaurantId) : undefined);
  const {
    items: retailItems,
    loading: retailLoading
  } = usePOSRetailProducts(isNatuCafe && restaurantId ? String(restaurantId) : undefined);
  const [search, setSearch] = useState('');
  const [retailSearch, setRetailSearch] = useState('');
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<MenuItem | null>(null);
  const [cart, setCart] = useState<PendingItem[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [armaLines, setArmaLines] = useState<string[]>(['', '', '', '', '', '']);
  // Reset whenever modal closes
  useEffect(() => {
    if (!isOpen) {
      setCart([]);
      setError(null);
      setSearch('');
      setRetailSearch('');
      setSelectedProduct(null);
      setActiveCategoryId(null);
      setArmaLines(['', '', '', '', '', '']);
    }
  }, [isOpen]);
  const activeCategories = useMemo(() => (categories || []).filter((c: any) => c.is_active).sort((a: any, b: any) => (a.sort_order || 0) - (b.sort_order || 0)), [categories]);
  const visibleItems = useMemo(() => {
    let list = (menuItems || []).filter((i: any) => i.is_active !== false);
    if (activeCategoryId && activeCategoryId !== ESTANTERIA_ID) {
      list = list.filter((i: any) => i.category_id === activeCategoryId);
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((i: any) => (i.name || '').toLowerCase().includes(q));
    }
    return list;
  }, [menuItems, activeCategoryId, search]);
  // Retail (Estantería) filter — search by external_pos_id OR name
  const visibleRetail = useMemo(() => {
    const q = retailSearch.trim().toLowerCase();
    if (!q) return retailItems || [];
    return (retailItems || []).filter((i: any) => {
      const name = (i.name || '').toLowerCase();
      const code = String(i.external_pos_id ?? '').toLowerCase();
      return name.includes(q) || code.includes(q);
    });
  }, [retailItems, retailSearch]);
  // Direct add for retail items (no options, no ProductDetailModal)
  const addRetailToCart = (item: any) => {
    setCart(prev => {
      const existingIdx = prev.findIndex(ci => ci.menuItem.id === item.id);
      if (existingIdx >= 0) {
        const next = [...prev];
        next[existingIdx] = {
          ...next[existingIdx],
          quantity: next[existingIdx].quantity + 1
        };
        return next;
      }
      return [...prev, {
        id: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        menuItem: item as MenuItem,
        quantity: 1,
        options: [],
        notes: item.external_pos_id ? `Cód. ${item.external_pos_id}` : ''
      }];
    });
  };
  const cartTotal = useMemo(() => cart.reduce((sum, ci) => {
    const optTotal = ci.options.reduce((s, o) => s + (Number(o.price_delta) || 0), 0);
    return sum + (Number(ci.menuItem.price) + optTotal) * ci.quantity;
  }, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, ci) => s + ci.quantity, 0), [cart]);
  const handleProductPicked = (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => {
    setCart(prev => [...prev, {
      id: `${item.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      menuItem: item,
      quantity,
      options,
      notes
    }]);
    setSelectedProduct(null);
  };
  const updateQty = (lineId: string, delta: number) => {
    setCart(prev => prev.map(ci => ci.id === lineId ? {
      ...ci,
      quantity: Math.max(0, ci.quantity + delta)
    } : ci).filter(ci => ci.quantity > 0));
  };
  const removeLine = (lineId: string) => {
    setCart(prev => prev.filter(ci => ci.id !== lineId));
  };
  const handleConfirm = async () => {
    if (!orderId || !restaurantId) {
      setError('Falta información de la orden.');
      return;
    }
    if (cart.length === 0) {
      setError('Agrega al menos un producto.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      // Track every order_item id we successfully insert. After all
      // inserts complete (and DB triggers have run), we re-verify the
      // rows actually still exist as active items. This catches silent
      // failures like trigger rollbacks, RLS deletes, or status-sync
      // triggers flipping our items to cancelled.
      const insertedItemIds: string[] = [];
      // 1) Insert each new order_item (mirrors create-order.ts shape)
      for (const ci of cart) {
        // Arma tu plato — ítems sintéticos sin menu_item_id real ni precio
        // por línea. El cajero captura UN solo precio por todo el grupo
        // durante el cobro en PayTableModal. Insertamos con price 0 igual
        // que WaiterPOSPage para mantener consistencia.
        const isArma = isArmaCartItem(ci.menuItem.id as string);
        const optTotal = ci.options.reduce((s, o) => s + (Number(o.price_delta) || 0), 0);
        const unitPrice = isArma ? 0 : Number(ci.menuItem.price) || 0;
        const lineTotal = isArma ? 0 : (unitPrice + optTotal) * ci.quantity;
        const {
          data: inserted,
          error: itemErr
        } = await supabase.from('order_items').insert({
          order_id: orderId,
          restaurant_id: Number(restaurantId),
          menu_item_id: isArma ? null : ci.menuItem.id,
          item_name_snapshot: ci.menuItem.name,
          unit_price_snapshot: unitPrice,
          quantity: ci.quantity,
          notes: ci.notes || null,
          line_total: lineTotal,
          status: 'pending'
        }).select('id').single();
        if (itemErr) throw itemErr;
        if (!inserted?.id) {
          throw new Error(`No se pudo confirmar la inserción de "${ci.menuItem.name}".`);
        }
        insertedItemIds.push(inserted.id);
        if (ci.options.length > 0 && inserted) {
          const optsPayload = ci.options.map(o => ({
            order_item_id: inserted.id,
            restaurant_id: Number(restaurantId),
            option_group: o.option_group || '',
            option_name: o.option_name || '',
            price_delta: Number(o.price_delta) || 0
          }));
          const {
            error: optErr
          } = await supabase.from('order_item_options').insert(optsPayload);
          if (optErr) {
            console.warn('[CabanaAddItems] options insert non-blocking error:', optErr);
          }
        }
      }
      // ─── Post-insert verification ─────────────────────────────────
      // Re-fetch the ids we just inserted to confirm they actually
      // persisted with a non-cancelled status. If a row went missing
      // (RLS delete, trigger rollback) or got flipped to cancelled by
      // a sync trigger, surface it clearly so the cashier knows the
      // item DID NOT make it into the order.
      if (insertedItemIds.length > 0) {
        const {
          data: verifyRows,
          error: verifyErr
        } = await supabase.from('order_items').select('id, status, item_name_snapshot, order_id').in('id', insertedItemIds);
        if (verifyErr) {
          console.warn('[CabanaAddItems] post-insert verify error (non-blocking):', verifyErr);
        } else {
          const verifyMap = new Map((verifyRows || []).map((r: any) => [String(r.id), r]));
          const missingIds = insertedItemIds.filter(id => !verifyMap.has(id));
          const cancelledRows = (verifyRows || []).filter((r: any) => ['cancelled', 'canceled', 'removed', 'voided', 'deleted'].includes(String(r.status || '').toLowerCase().trim()));
          const wrongOrderRows = (verifyRows || []).filter((r: any) => String(r.order_id) !== String(orderId));
          if (missingIds.length > 0 || cancelledRows.length > 0 || wrongOrderRows.length > 0) {
            // ⚠️ NON-BLOCKING verification warning.
            //
            // The INSERT already returned a successful id from Supabase
            // (we wouldn't reach this code otherwise). The post-insert
            // SELECT may legitimately miss rows for reasons that DON'T
            // mean the data is missing from the DB:
            //   • RLS SELECT policy stricter than the INSERT policy
            //     (e.g. cashier can write but not read certain rows).
            //   • PostgREST short-window cache staleness.
            //   • AFTER-INSERT triggers that move/transform the row.
            //
            // Throwing here would block the cashier from a workflow
            // whose data IS persisted. Instead we log loudly so it can
            // be diagnosed, but let the rest of the flow proceed: the
            // subtotal recompute below pulls fresh data, and Cobrar
            // Mesa now reads order_items directly (see useTableOrderItems
            // hardening) so the truth on disk wins.
            console.warn('[CabanaAddItems] ⚠️ Post-insert verification suspicious (non-blocking — DB write already succeeded)', {
              insertedItemIds,
              missingIds,
              cancelledRows,
              wrongOrderRows
            });
          }
        }
      }
      // 2) Recompute order subtotal/total from all NON-cancelled items
      const {
        data: allItems,
        error: fetchErr
      } = await supabase.from('order_items').select('line_total, status').eq('order_id', orderId);
      if (fetchErr) throw fetchErr;
      const newSubtotal = (allItems || []).filter((it: any) => it.status !== 'cancelled').reduce((s: number, it: any) => s + (Number(it.line_total) || 0), 0);
      const {
        data: orderRow
      } = await supabase.from('orders').select('delivery_fee, status').eq('id', orderId).maybeSingle();
      const deliveryFee = Number(orderRow?.delivery_fee) || 0;
      const newTotal = newSubtotal + deliveryFee;
      const updatePayload: Record<string, any> = {
        subtotal: newSubtotal,
        total: newTotal,
        updated_at: new Date().toISOString()
      };
      // If the order was already marked 'ready', bump it back to
      // 'preparing' so the KDS shows it again with the new pending items.
      if (orderRow?.status === 'ready') {
        updatePayload.status = 'preparing';
      }
      const {
        error: updErr
      } = await supabase.from('orders').update(updatePayload).eq('id', orderId);
      if (updErr) throw updErr;
      // ─── Rancho San Francisco (id 28) — Print job (with diagnostics) ─
      // Cuando se ADICIONAN productos a una orden existente desde
      // /admin/rancho-san-francisco/tables, creamos una fila pendiente
      // en order_print_jobs para que el bridge imprima el ticket.
      // Solo aplica al Rancho. No-bloqueante.
      console.log('[RANCHO_PRINT_TABLES] restaurantId:', restaurantId);
      console.log('[RANCHO_PRINT_TABLES] orderId (add-items):', orderId);
      const isRancho28AddItems = Number(restaurantId) === 28 || String(restaurantId) === '28';
      if (isRancho28AddItems && orderId) {
        try {
          console.log('[RANCHO_PRINT_TABLES] Creating print job (add-items) for order', orderId);
          // SIN existence check bloqueante. Vamos directo al INSERT.
          {
            // Construimos el payload SIN dependencia obligatoria de
            // fetches con joins. Si el fetch falla, igual insertamos.
            // 1) Fetch simple de la orden (SIN joins)
            let fullOrder: any = null;
            try {
              const {
                data: ord,
                error: ordErr
              } = await supabase.from('orders').select('*').eq('id', orderId).maybeSingle();
              if (ordErr) {
                console.warn('[RANCHO_PRINT_TABLES] order fetch error (non-blocking)', ordErr);
              } else {
                fullOrder = ord;
              }
            } catch (e) {
              console.warn('[RANCHO_PRINT_TABLES] order fetch threw (non-blocking)', e);
            }
            // 2) Fetch simple de order_items (SIN joins). Si falla,
            //    fallback al cart actual que ya acabamos de adicionar.
            let orderItems: any[] = [];
            try {
              const {
                data: its,
                error: itsErr
              } = await supabase.from('order_items').select('*').eq('order_id', orderId);
              if (itsErr) {
                console.warn('[RANCHO_PRINT_TABLES] items fetch error (non-blocking)', itsErr);
              } else {
                orderItems = its || [];
              }
            } catch (e) {
              console.warn('[RANCHO_PRINT_TABLES] items fetch threw (non-blocking)', e);
            }
            // Fallback a cart si la DB no devolvió items
            if (orderItems.length === 0 && cart.length > 0) {
              console.log('[RANCHO_PRINT_TABLES] Using cart as items fallback (add-items)');
              orderItems = cart.map(ci => {
                const unitPrice = Number(ci.menuItem.price) || 0;
                const optTotal = (ci.options || []).reduce((s, o: any) => s + (Number(o.price_delta) || 0), 0);
                return {
                  menu_item_id: ci.menuItem.id,
                  item_name_snapshot: ci.menuItem.name,
                  unit_price_snapshot: unitPrice,
                  quantity: ci.quantity,
                  notes: ci.notes || null,
                  line_total: (unitPrice + optTotal) * ci.quantity,
                  options: ci.options || []
                };
              });
            }
            // order_print_jobs.order_id es UUID. orderId acá es numérico
            // (id interno). Usamos tracking_token (UUID) que viene en
            // fullOrder. Si no lo tenemos, no podemos insertar la fila.
            const orderUuid = (fullOrder as any)?.tracking_token ?? null;
            console.log('[RANCHO_PRINT_TABLES] resolved ids (add-items) — numeric id:', orderId, ' uuid (tracking_token):', orderUuid);
            const insertPayload = {
              restaurant_id: 28,
              order_id: orderUuid,
              order_number: (fullOrder as any)?.order_number ?? null,
              status: 'pending',
              payload: {
                order_internal_id: orderId,
                order: fullOrder ?? null,
                items: orderItems,
                customer_name: (fullOrder as any)?.customer_name ?? null,
                customer_phone: (fullOrder as any)?.customer_phone ?? null,
                delivery_type: (fullOrder as any)?.delivery_type ?? 'dine_in',
                payment_method: (fullOrder as any)?.payment_method ?? null,
                table_number: null,
                notes: (fullOrder as any)?.notes ?? null,
                subtotal: (fullOrder as any)?.subtotal ?? 0,
                delivery_fee: (fullOrder as any)?.delivery_fee ?? 0,
                total: (fullOrder as any)?.total ?? 0,
                created_at: (fullOrder as any)?.created_at ?? new Date().toISOString(),
                trigger: 'add_items'
              }
            };
            console.log('[RANCHO_PRINT_TABLES] Inserting add_items print job:', insertPayload);
            const insertResponse = await supabase.from('order_print_jobs').insert(insertPayload).select('id').maybeSingle();
            console.log('[RANCHO_PRINT_TABLES] insert response data:', insertResponse?.data);
            console.log('[RANCHO_PRINT_TABLES] insert response error:', insertResponse?.error);
            if (insertResponse?.error) {
              console.error('[RANCHO_PRINT_TABLES] ❌ Failed to create print job — full error object:', JSON.stringify(insertResponse.error, Object.getOwnPropertyNames(insertResponse.error)));
            } else {
              console.log('[RANCHO_PRINT_TABLES] ✅ Print job created successfully:', insertResponse?.data);
            }
          }
        } catch (printErr) {
          console.error('[RANCHO_PRINT_TABLES] Unexpected error creating print job', printErr);
        }
      } else {
        console.log('[RANCHO_PRINT_TABLES] Skipping add-items print job — not Rancho or no orderId', {
          isRancho28AddItems,
          orderId
        });
      }
      // ─── NatuCafé (id 19) — Adición a mesa abierta ─────────────────
      // La mesa ya tuvo su comanda inicial impresa por el trigger de
      // DB (SETUP_NATUCAFE_PRINT_ON_ITEMS_TRIGGER). Ese trigger dedupea
      // por trigger='natucafe_new' y no vuelve a disparar para nuevos
      // items. Acá creamos un print job MANUAL con SÓLO los items que
      // se acaban de agregar — el bridge los imprime con la mesa
      // correcta y un bloque 'NOTA: ADICIÓN' para que cocina/barra
      // sepa que es un ticket extra, no una repetición.
      // No-bloqueante: si falla, los items ya quedaron persistidos.
      const isNatuCafe19 = Number(restaurantId) === 19 || String(restaurantId) === '19';
      if (isNatuCafe19 && orderId) {
        try {
          const addedItemsForPrint = cart.map(ci => {
            const isArma = isArmaCartItem(ci.menuItem.id as string);
            const optTotal = ci.options.reduce((s, o) => s + (Number(o.price_delta) || 0), 0);
            const unitPrice = isArma ? 0 : Number(ci.menuItem.price) || 0;
            const lineTotal = isArma ? 0 : (unitPrice + optTotal) * ci.quantity;
            return {
              menu_item_id: isArma ? null : ci.menuItem.id as string,
              item_name_snapshot: ci.menuItem.name,
              unit_price_snapshot: unitPrice,
              quantity: ci.quantity,
              notes: ci.notes || null,
              line_total: lineTotal,
              station_code: (ci.menuItem as any).station_code ?? null,
              selected_options: (ci.options || []).map((o: any) => ({
                option_group: o.option_group || '',
                option_name: o.option_name || '',
                price_delta: Number(o.price_delta) || 0
              }))
            };
          });
          const result = await createNatuCafeAdditionPrintJob({
            restaurantId,
            orderId,
            addedItems: addedItemsForPrint
          });
          if (!result.ok) {
            console.warn('[NATUCAFE_ADD_PRINT][CabanaAddItems] skipped/failed:', result.reason);
          } else {
            console.log('[NATUCAFE_ADD_PRINT][CabanaAddItems] ✅ print job created:', result.jobId);
          }
        } catch (printErr) {
          console.warn('[NATUCAFE_ADD_PRINT][CabanaAddItems] exception:', printErr);
        }
      }
      onAdded();
      onClose();
    } catch (e: any) {
      console.error('[CabanaAddItems] add error:', e);
      setError(e?.message || 'No se pudieron agregar los productos.');
    } finally {
      setSubmitting(false);
    }
  };
  // Helper to detect synthetic arma cart lines.
  const isArmaCartItem = (id: string) => typeof id === 'string' && id.startsWith('__custom__');
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-266">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className={cn('fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4',
    // While the product detail picker is open, hide this overlay
    // so the picker (Modal.tsx uses z-50) actually receives clicks.
    selectedProduct && 'invisible pointer-events-none')} onClick={onClose} data-id="element-267">
        <motion.div initial={{
        y: 40,
        opacity: 0
      }} animate={{
        y: 0,
        opacity: 1
      }} exit={{
        y: 40,
        opacity: 0
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 28
      }} className="bg-white w-full sm:max-w-3xl sm:rounded-2xl rounded-t-2xl shadow-2xl max-h-[94vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-268">
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between shrink-0" data-id="element-269">
            <div className="flex items-center gap-3" data-id="element-270">
              <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center" data-id="element-271">
                <Plus className="w-5 h-5 text-amber-600" data-id="element-272" />
              </div>
              <div data-id="element-273">
                <h2 className="text-lg font-black text-gray-900 leading-tight" data-id="element-274">
                  Adicionar Productos
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-275">
                  Se agregan a la orden activa y se envían a cocina.
                </p>
              </div>
            </div>
            <button onClick={onClose} disabled={submitting} className="w-9 h-9 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition-colors disabled:opacity-40" aria-label="Cerrar" data-id="element-276">
              <X className="w-5 h-5" data-id="element-277" />
            </button>
          </div>

          {/* Search + Categories */}
          <div className="px-5 pt-3 pb-2 shrink-0 space-y-3" data-id="element-278">
            {/* Hide the regular menu search when Estantería is active; it has its own search */}
            {activeCategoryId !== ESTANTERIA_ID && <div className="relative" data-id="element-279">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-280" />
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar producto…" className="w-full pl-9 pr-3 h-10 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-amber-400" data-id="element-281" />
              </div>}
            <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 -mx-1 px-1" data-id="element-282">
              <button onClick={() => setActiveCategoryId(null)} className={cn('h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors', activeCategoryId === null ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')} data-id="element-283">
                Todos
              </button>
              {isNatuCafe && <button onClick={() => setActiveCategoryId(ESTANTERIA_ID)} className={cn('h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors flex items-center gap-1', activeCategoryId === ESTANTERIA_ID ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100')} data-id="element-284">
                  <Package className="w-3.5 h-3.5" data-id="element-285" />
                  Estantería
                </button>}
              {isRancho && <button onClick={() => setActiveCategoryId(ARMA_TU_PLATO_ID)} className={cn('h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors', activeCategoryId === ARMA_TU_PLATO_ID ? 'bg-amber-500 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100')} data-id="element-286">
                  Arma tu plato
                </button>}
              {activeCategories.map((c: any) => <button key={c.id} onClick={() => setActiveCategoryId(c.id)} className={cn('h-8 px-3 rounded-full text-xs font-bold whitespace-nowrap transition-colors', activeCategoryId === c.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200')} data-id="element-287">
                  {c.name}
                </button>)}
            </div>
          </div>

          {/* Body: product grid OR Arma tu plato panel OR Estantería */}
          <div className="flex-1 overflow-y-auto px-5 pb-3" data-id="element-288">
            {isNatuCafe && activeCategoryId === ESTANTERIA_ID ? <div className="py-3 space-y-3" data-id="element-289">
                <div className="relative" data-id="element-290">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-291" />
                  <input type="text" value={retailSearch} onChange={e => setRetailSearch(e.target.value)} placeholder="Buscar por código o nombre…" autoFocus className="w-full pl-9 pr-3 h-11 rounded-xl border-2 border-emerald-200 text-sm focus:outline-none focus:border-emerald-500" data-id="element-292" />
                </div>
                {retailLoading ? <div className="flex justify-center py-12 text-gray-400" data-id="element-293">
                    <Loader2 className="w-6 h-6 animate-spin" data-id="element-294" />
                  </div> : visibleRetail.length === 0 ? <div className="text-center py-12 text-gray-500 text-sm" data-id="element-295">
                    {retailSearch ? 'Sin resultados para esa búsqueda.' : 'No hay productos de Estantería cargados.'}
                  </div> : <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-296">
                    {visibleRetail.map((item: any) => {
                const stock = item.stock_quantity === null || item.stock_quantity === undefined ? null : Number(item.stock_quantity);
                const lowStock = stock !== null && stock <= 0;
                return <button key={item.id} onClick={() => addRetailToCart(item)} className="text-left p-3 rounded-xl border border-gray-200 hover:border-emerald-400 hover:bg-emerald-50/40 active:scale-[0.98] transition-all flex flex-col gap-1" data-id="element-297">
                          <div className="flex items-start justify-between gap-1" data-id="element-298">
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded tabular-nums" data-id="element-299">
                              {item.external_pos_id || '—'}
                            </span>
                            {stock !== null && <span className={cn('text-[10px] font-bold tabular-nums', lowStock ? 'text-red-600' : 'text-gray-400')} data-id="element-300">
                                {stock}
                              </span>}
                          </div>
                          <p className="font-bold text-sm text-gray-900 leading-snug line-clamp-2" data-id="element-301">
                            {item.name}
                          </p>
                          <p className="text-xs font-black text-emerald-600 tabular-nums mt-auto" data-id="element-302">
                            {formatCurrency(item.price)}
                          </p>
                        </button>;
              })}
                  </div>}
              </div> : isRancho && activeCategoryId === ARMA_TU_PLATO_ID ? <div className="py-4" data-id="element-303">
                <ArmaTuPlatoPanel lines={armaLines} setLines={setArmaLines} onAddToCart={filledLines => {
              const newItems: PendingItem[] = filledLines.map(text => {
                const syntheticId = `__custom__${crypto.randomUUID()}`;
                return {
                  id: `${syntheticId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                  menuItem: {
                    id: syntheticId,
                    name: text,
                    price: 0,
                    is_active: true,
                    is_customizable: false,
                    category_id: ARMA_TU_PLATO_ID,
                    restaurant_id: Number(restaurantId) || 0
                  } as any,
                  quantity: 1,
                  options: [],
                  notes: ''
                };
              });
              setCart(prev => [...prev, ...newItems]);
              setArmaLines(['', '', '', '', '', '']);
            }} data-id="element-304" />
              </div> : menuLoading ? <div className="flex justify-center py-16 text-gray-400" data-id="element-305">
                <Loader2 className="w-6 h-6 animate-spin" data-id="element-306" />
              </div> : visibleItems.length === 0 ? <div className="text-center py-16 text-gray-500 text-sm" data-id="element-307">
                Ningún producto coincide con la búsqueda.
              </div> : <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-308">
                {visibleItems.map((item: any) => <button key={item.id} onClick={() => setSelectedProduct(item)} className="text-left p-3 rounded-xl border border-gray-200 hover:border-amber-400 hover:bg-amber-50/40 active:scale-[0.98] transition-all flex flex-col gap-1" data-id="element-309">
                    <p className="font-bold text-sm text-gray-900 leading-snug line-clamp-2" data-id="element-310">
                      {item.name}
                    </p>
                    <p className="text-xs font-black text-amber-600 tabular-nums mt-auto" data-id="element-311">
                      {formatCurrency(item.price)}
                    </p>
                  </button>)}
              </div>}
          </div>

          {/* Pending cart */}
          {cart.length > 0 && <div className="border-t border-gray-100 px-5 py-3 max-h-[28vh] overflow-y-auto shrink-0 bg-gray-50" data-id="element-312">
              <div className="flex items-center gap-2 mb-2" data-id="element-313">
                <ShoppingBag className="w-4 h-4 text-gray-500" data-id="element-314" />
                <p className="text-xs font-bold text-gray-700 uppercase tracking-wider" data-id="element-315">
                  Por adicionar ({cartCount})
                </p>
              </div>
              <div className="space-y-2" data-id="element-316">
                {cart.map(ci => {
              const optTotal = ci.options.reduce((s, o) => s + (Number(o.price_delta) || 0), 0);
              const lineTotal = (Number(ci.menuItem.price) + optTotal) * ci.quantity;
              return <div key={ci.id} className="bg-white rounded-lg border border-gray-200 px-3 py-2 flex items-start gap-2" data-id="element-317">
                      <div className="flex-1 min-w-0" data-id="element-318">
                        <p className="text-sm font-bold text-gray-900 truncate" data-id="element-319">
                          {ci.menuItem.name}
                        </p>
                        {ci.options.length > 0 && <p className="text-[11px] text-gray-500 leading-tight" data-id="element-320">
                            {ci.options.map(o => o.option_name).join(' · ')}
                          </p>}
                        {ci.notes && <p className="text-[11px] text-amber-700 italic" data-id="element-321">
                            "{ci.notes}"
                          </p>}
                      </div>
                      <div className="flex items-center gap-1 shrink-0" data-id="element-322">
                        <button onClick={() => updateQty(ci.id, -1)} className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700" data-id="element-323">
                          <Minus className="w-3.5 h-3.5" data-id="element-324" />
                        </button>
                        <span className="w-6 text-center font-black text-sm text-gray-900 tabular-nums" data-id="element-325">
                          {ci.quantity}
                        </span>
                        <button onClick={() => updateQty(ci.id, +1)} className="w-7 h-7 rounded-md bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-700" data-id="element-326">
                          <Plus className="w-3.5 h-3.5" data-id="element-327" />
                        </button>
                        <button onClick={() => removeLine(ci.id)} className="w-7 h-7 rounded-md text-red-500 hover:bg-red-50 flex items-center justify-center ml-1" aria-label="Quitar" data-id="element-328">
                          <Trash2 className="w-3.5 h-3.5" data-id="element-329" />
                        </button>
                      </div>
                      <span className="text-sm font-black text-gray-900 tabular-nums shrink-0 w-20 text-right" data-id="element-330">
                        {formatCurrency(lineTotal)}
                      </span>
                    </div>;
            })}
              </div>
            </div>}

          {/* Footer */}
          <div className="border-t border-gray-100 px-5 py-4 bg-white shrink-0" data-id="element-331">
            {error && <div className="mb-3 p-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2" data-id="element-332">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" data-id="element-333" />
                <span data-id="element-334">{error}</span>
              </div>}
            <div className="flex items-center justify-between mb-3" data-id="element-335">
              <span className="text-sm font-bold text-gray-600" data-id="element-336">
                Total a adicionar
              </span>
              <span className="text-2xl font-black text-amber-600 tabular-nums" data-id="element-337">
                {formatCurrency(cartTotal)}
              </span>
            </div>
            <button onClick={handleConfirm} disabled={submitting || cart.length === 0} className="w-full h-12 rounded-xl font-black text-sm bg-gray-900 hover:bg-gray-800 text-white active:scale-95 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-id="element-338">
              {submitting ? <>
                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-339" />
                  Agregando…
                </> : <>
                  <Check className="w-5 h-5" data-id="element-340" />
                  Adicionar a la orden
                </>}
            </button>
          </div>
        </motion.div>
      </motion.div>

      {/* Product detail picker — reuses existing component */}
      {selectedProduct && <ProductDetailModal isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} item={selectedProduct} onAdd={handleProductPicked} data-id="element-341" />}
    </AnimatePresence>;
}